import {
  Badge,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from "@fluentui/react-components";
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
  Dismiss20Regular,
} from "@fluentui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "@/lib/apiClient";
import { lookupTradeMatchesForEmail } from "@/api/trades";
import type {
  TradeMatchCandidate,
  TradeMatchQueryEcho,
  TradeMatchResponse,
} from "@/types/domain";

interface TradeMatchSuggestionsProps {
  emailId: string;
  onPick: (tradeKey: string) => void;
  onClose: () => void;
  disabled?: boolean;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

function formatDateRange(
  from: string | null,
  to: string | null,
): string | null {
  if (!from && !to) return null;
  if (from && to && from.slice(0, 10) === to.slice(0, 10))
    return formatDate(from);
  return `${formatDate(from)} → ${formatDate(to)}`;
}

/** Map a *rounded* score to a Fluent Badge colour. Callers must round
 * before calling so the badge digit and its colour band line up — the
 * displayed digit is rounded for readability, and a 79.7 should not show
 * "80" in the 60-79 colour. */
function scoreColor(
  roundedScore: number,
): "success" | "brand" | "subtle" | "warning" {
  if (roundedScore >= 80) return "success"; // green — strong
  if (roundedScore >= 60) return "brand"; // blue  — likely
  if (roundedScore >= 40) return "warning"; // amber — weak
  return "subtle"; // grey  — unreachable above default cutoff
}

function trio(c: TradeMatchCandidate): string {
  return `${c.tradeNumber}.${c.orderNumber}.${c.itemNumber}`;
}

/** Inline dropdown shown below the email-level trade-key Input when the
 * operator wants to discover candidate trades. Stays open until the
 * operator picks a row or explicitly dismisses it. */
export function TradeMatchSuggestions({
  emailId,
  onPick,
  onClose,
  disabled,
}: TradeMatchSuggestionsProps) {
  const query = useQuery<TradeMatchResponse>({
    queryKey: ["trade-matches", emailId],
    queryFn: ({ signal }) =>
      lookupTradeMatchesForEmail(emailId, { signal }),
    staleTime: 5 * 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 503) return false;
      return failureCount < 2;
    },
  });

  const containerStyle: React.CSSProperties = {
    marginTop: 8,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: 6,
    background: tokens.colorNeutralBackground1,
    padding: 8,
    maxHeight: 480,
    overflowY: "auto",
  };

  const header = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 6,
      }}
    >
      <Text
        size={200}
        style={{ color: tokens.colorNeutralForeground2 }}
      >
        Fuzzy matches from Fabric. Pick a trade and confirm direction (S
        or B):
      </Text>
      <Button
        size="small"
        appearance="subtle"
        icon={<Dismiss20Regular />}
        onClick={onClose}
        aria-label="Close suggestions"
        title="Close suggestions"
      />
    </div>
  );

  if (query.isLoading) {
    return (
      <div style={containerStyle}>
        {header}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Spinner size="tiny" />
          <Text size={200}>Searching Fabric for trade matches…</Text>
        </div>
      </div>
    );
  }

  if (query.isError) {
    const isUnavailable =
      query.error instanceof ApiError && query.error.status === 503;
    return (
      <div style={containerStyle}>
        {header}
        <MessageBar intent={isUnavailable ? "warning" : "error"}>
          <MessageBarBody>
            {isUnavailable
              ? "Trade match lookup is unavailable right now. Type a trade key directly."
              : query.error instanceof Error
                ? query.error.message
                : "Match lookup failed."}
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  const data = query.data;
  const candidates = data?.candidates ?? [];
  if (candidates.length === 0) {
    return (
      <div style={containerStyle}>
        {header}
        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
          No matches found. The email needs at least vessel, counterparty,
          or laycan to fuzzy-match against Fabric.
        </Text>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {header}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {candidates.map((c) => (
          <SuggestionRow
            key={trio(c)}
            candidate={c}
            queryEcho={data!.query}
            onPick={onPick}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

interface SuggestionRowProps {
  candidate: TradeMatchCandidate;
  queryEcho: TradeMatchQueryEcho;
  onPick: (tradeKey: string) => void;
  disabled?: boolean;
}

function SuggestionRow({
  candidate: c,
  queryEcho,
  onPick,
  disabled,
}: SuggestionRowProps) {
  const [expanded, setExpanded] = useState(false);
  const key = trio(c);
  const dates = formatDateRange(c.scheduleFrom, c.scheduleTo);
  const roundedScore = Math.round(c.score);

  const summaryLine = [
    c.product ?? c.productGroup,
    c.loadPort && c.dischargePort && c.loadPort !== c.dischargePort
      ? `${c.loadPort} → ${c.dischargePort}`
      : (c.loadPort ?? c.dischargePort),
    dates,
    c.parcelNumbers.length > 1 ? `${c.parcelNumbers.length} parcels` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      style={{
        border: `1px solid ${tokens.colorNeutralStroke3}`,
        borderRadius: 4,
        padding: 8,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto 1fr auto",
          gap: 8,
          alignItems: "center",
        }}
      >
        <Button
          size="small"
          appearance="subtle"
          icon={
            expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />
          }
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Hide preview" : "Show preview"}
          title={expanded ? "Hide preview" : "Show preview"}
        />
        <Badge
          appearance="filled"
          color={scoreColor(roundedScore)}
          size="medium"
          title={`Match score ${roundedScore}/100 (raw ${c.score.toFixed(1)}).`}
        >
          {roundedScore}
        </Badge>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
          }}
        >
          <Text
            weight="semibold"
            truncate
            block
            wrap={false}
            style={{ overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {key}
            {c.motName ? ` · ${c.motName}` : ""}
            {c.cptyShortName ? ` · ${c.cptyShortName}` : ""}
          </Text>
          {summaryLine ? (
            <Text
              size={200}
              style={{ color: tokens.colorNeutralForeground2 }}
              truncate
              block
              wrap={false}
            >
              {summaryLine}
            </Text>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <Button
            size="small"
            appearance="outline"
            onClick={() => onPick(`S${key}`)}
            disabled={disabled}
            title={`Commit as S${key} (sale)`}
          >
            S
          </Button>
          <Button
            size="small"
            appearance="outline"
            onClick={() => onPick(`B${key}`)}
            disabled={disabled}
            title={`Commit as B${key} (buy)`}
          >
            B
          </Button>
        </div>
      </div>
      {expanded ? (
        <ComparisonTable candidate={c} queryEcho={queryEcho} />
      ) : null}
    </div>
  );
}

interface ComparisonTableProps {
  candidate: TradeMatchCandidate;
  queryEcho: TradeMatchQueryEcho;
}

interface ComparisonRow {
  field: string;
  emailValue: string;
  fabricValue: string;
  score: number | null;
}

function joinList(items: readonly string[]): string {
  return items.filter((i) => i && i.trim()).join(", ") || "—";
}

function fabricCpty(c: TradeMatchCandidate): string {
  const short = c.cptyShortName ?? "";
  const legal = c.cptyLegalName ?? "";
  if (short && legal && short !== legal) return `${short} (${legal})`;
  return short || legal || "—";
}

function fabricProduct(c: TradeMatchCandidate): string {
  const p = c.product ?? "";
  const g = c.productGroup ?? "";
  if (p && g && p !== g) return `${p} (${g})`;
  return p || g || "—";
}

function fabricPorts(c: TradeMatchCandidate): string {
  const load = c.loadPort ?? "";
  const disch = c.dischargePort ?? "";
  if (load && disch && load !== disch) return `${load} → ${disch}`;
  return load || disch || "—";
}

function fabricDateRange(c: TradeMatchCandidate): string {
  const range = formatDateRange(c.scheduleFrom, c.scheduleTo);
  return range ? `Schedule ${range}` : "—";
}

function emailLaycan(q: TradeMatchQueryEcho): string {
  if (q.laycanStart || q.laycanEnd) {
    const range = formatDateRange(q.laycanStart, q.laycanEnd);
    return range ? `Laycan ${range}` : "—";
  }
  if (q.cpDate) return `CP date ${formatDate(q.cpDate)}`;
  return "—";
}

function ComparisonTable({ candidate, queryEcho }: ComparisonTableProps) {
  const rows: ComparisonRow[] = [
    {
      field: "Vessel",
      emailValue: queryEcho.vesselName ?? "—",
      fabricValue: candidate.motName ?? "—",
      score: candidate.scoreBreakdown.vessel ?? null,
    },
    {
      field: "Counterparty",
      emailValue: joinList(queryEcho.counterparties),
      fabricValue: fabricCpty(candidate),
      score: candidate.scoreBreakdown.counterparty ?? null,
    },
    {
      field: "Product",
      emailValue: queryEcho.productGrade ?? "—",
      fabricValue: fabricProduct(candidate),
      score: candidate.scoreBreakdown.product ?? null,
    },
    {
      field: "Ports",
      emailValue: joinList(queryEcho.ports),
      fabricValue: fabricPorts(candidate),
      score: candidate.scoreBreakdown.ports ?? null,
    },
    {
      field: "Laycan / Schedule",
      emailValue: emailLaycan(queryEcho),
      fabricValue: fabricDateRange(candidate),
      score: candidate.scoreBreakdown.date ?? null,
    },
  ];

  const headerCell: React.CSSProperties = {
    padding: "4px 8px",
    color: tokens.colorNeutralForeground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    fontWeight: 600,
    fontSize: 12,
    textAlign: "left",
  };
  const cell: React.CSSProperties = {
    padding: "4px 8px",
    verticalAlign: "top",
    fontSize: 12,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  };

  return (
    <div
      style={{
        borderTop: `1px dashed ${tokens.colorNeutralStroke2}`,
        paddingTop: 6,
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...headerCell, width: "20%" }}>Field</th>
            <th style={headerCell}>From email</th>
            <th style={headerCell}>From Fabric</th>
            <th style={{ ...headerCell, width: "70px", textAlign: "right" }}>
              Score
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field}>
              <td style={{ ...cell, color: tokens.colorNeutralForeground2 }}>
                {r.field}
              </td>
              <td style={cell}>{r.emailValue}</td>
              <td style={cell}>{r.fabricValue}</td>
              <td style={{ ...cell, textAlign: "right" }}>
                {r.score == null ? (
                  <Text
                    size={200}
                    style={{ color: tokens.colorNeutralForeground3 }}
                  >
                    —
                  </Text>
                ) : (
                  r.score.toFixed(0)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {candidate.imoNum || candidate.parcelNumbers.length ? (
        <div
          style={{
            marginTop: 6,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            color: tokens.colorNeutralForeground2,
            fontSize: 12,
          }}
        >
          {candidate.imoNum ? <span>IMO: {candidate.imoNum}</span> : null}
          {candidate.parcelNumbers.length ? (
            <span>
              Parcels: {candidate.parcelNumbers.join(", ")}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
